// Shared "not built yet" affordance for demo-shell buttons that don't do anything real.
// Call useNotImplemented() to get a notify(feature) function: it fires a posthog event
// and opens a shared modal telling the user the feature isn't ready.
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import posthog from "@/lib/posthog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/enhanced-button";

type NotifyFn = (feature: string) => void;

const NotImplementedContext = createContext<NotifyFn | null>(null);

export function NotImplementedProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [feature, setFeature] = useState<string | null>(null);

  const notify: NotifyFn = (featureName) => {
    posthog.capture("not_implemented_clicked", { feature: featureName });
    setFeature(featureName);
    setOpen(true);
  };

  // Fires when the modal actually renders, separate from the click that requested it —
  // mirrors the click/view split used elsewhere (nav_section_clicked vs section_viewed).
  useEffect(() => {
    if (open && feature) posthog.capture("not_implemented_shown", { feature });
  }, [open, feature]);

  return (
    <NotImplementedContext.Provider value={notify}>
      {children}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Not available yet</DialogTitle>
            <DialogDescription>
              We're sorry, but {feature ? `"${feature}"` : "this"} isn't developed yet. We're working on it!
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setOpen(false)}>Got it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </NotImplementedContext.Provider>
  );
}

export function useNotImplemented(): NotifyFn {
  const notify = useContext(NotImplementedContext);
  if (!notify) throw new Error("useNotImplemented must be used within a NotImplementedProvider");
  return notify;
}
